/**
 * Paperbloom customizer renderer.
 *
 * Copy of ~/paperbloom/src/browse/ui/renderer.js (+ fittedDrawRect from
 * image-fit.js) so the storefront preview composites exactly like the studio's
 * production replay. Only change: template files resolve against the studio
 * origin (setFileOrigin) instead of the page origin. Keep in sync with the
 * studio when its renderer changes.
 */

let FILE_ORIGIN = "";
export function setFileOrigin(origin) {
  FILE_ORIGIN = String(origin || "").replace(/\/+$/, "");
}

/**
 * Composites a template onto a canvas.
 *
 * Layers are drawn strictly in `zIndex` order across all element types, which
 * is how the source data expresses stacking — a product mockup's shadow and
 * overlay layers can sit above some text and below other text.
 */

function fittedDrawRect(imgW, imgH, boxW, boxH, { fit = "cover", zoom = 1, panX = 0, panY = 0 } = {}) {
  if (!imgW || !imgH || !boxW || !boxH) {
    return { x: -boxW / 2, y: -boxH / 2, w: boxW, h: boxH };
  }
  const base = fit === "contain" ? Math.min(boxW / imgW, boxH / imgH) : Math.max(boxW / imgW, boxH / imgH);
  const scale = base * Math.max(0.25, Number(zoom) || 1);
  const w = imgW * scale;
  const h = imgH * scale;
  const extraX = Math.max(0, w - boxW);
  const extraY = Math.max(0, h - boxH);
  return {
    x: -w / 2 - (Number(panX) || 0) * extraX / 2,
    y: -h / 2 - (Number(panY) || 0) * extraY / 2,
    w,
    h,
  };
}

/**
 * Theme only: artwork plates are stored at print size (6000 px, several MB). On screen a small copy from the
 * relay's /api/plate is enough and loads many times faster; the print is still made from the full file by the
 * studio. 0 = always the full file (the default, and what the builder's own copy of this file uses).
 */
let PLATE_WIDTH = 0;
export function setPlateWidth(px) {
  PLATE_WIDTH = px === 1200 || px === 2000 ? px : 0;
}
const PLATE_PATH = /^exports\/artwork-plates\/[A-Za-z0-9._-]+\.(png|webp|jpg)$/;

/** Buyer photos arrive as absolute URLs; template assets as mirror-relative paths. */
const ABSOLUTE_URL = /^(?:https?:|data:|blob:)/i;

export const fileUrl = (rel) =>
  ABSOLUTE_URL.test(String(rel))
    ? String(rel)
    : `${FILE_ORIGIN}/files/${String(rel).split("/").map(encodeURIComponent).join("/")}`;

const imageCache = new Map();
const fontCache = new Map();

/**
 * Assets that failed to load during this session.
 *
 * Rendering tolerates a missing file so the editor degrades gracefully, which
 * means a print file can lose artwork without anything throwing. The print
 * path checks this to refuse silently-incomplete output.
 */
const missingFiles = new Set();

export function missingAssets() {
  return [...missingFiles, ...missingSvg];
}

export function loadImage(rel) {
  if (imageCache.has(rel)) return imageCache.get(rel);
  const promise = new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    // A missing layer shouldn't abort the whole render: the editor stays usable
    // and shows what it can. The omission is recorded rather than swallowed so
    // the print path can refuse to ship an incomplete file.
    const small = PLATE_WIDTH && PLATE_PATH.test(String(rel));
    img.onerror = () => {
      // The small copy is a convenience: if it can't be had, fall back to the full file once.
      if (small && !img.dataset.full) {
        img.dataset.full = "1";
        img.src = fileUrl(rel);
        return;
      }
      missingFiles.add(rel);
      resolve(null);
    };
    const src = small ? `${FILE_ORIGIN}/api/plate?p=${encodeURIComponent(rel)}&w=${PLATE_WIDTH}` : fileUrl(rel);
    // A photo hosted elsewhere would taint the canvas and make the print file
    // unexportable, so it is requested in a mode that keeps the canvas clean.
    if (ABSOLUTE_URL.test(src) && !src.startsWith("data:") && !src.startsWith("blob:")) {
      try {
        if (new URL(src, location.href).origin !== location.origin) img.crossOrigin = "anonymous";
      } catch {
        /* a malformed URL simply fails to load below */
      }
    }
    img.src = src;
  });
  imageCache.set(rel, promise);
  return promise;
}

const svgTextCache = new Map();
const recoloredCache = new Map();

// `stop-color` matters too: some backgrounds are gradients.
const FILL_PATTERN = /((?:fill|stop-color)\s*[:=]\s*["']?)(#[0-9a-fA-F]{3,8})/g;

const missingSvg = new Set();

async function svgText(rel) {
  if (!rel || missingSvg.has(rel)) return null;
  if (!svgTextCache.has(rel)) {
    svgTextCache.set(
      rel,
      fetch(fileUrl(rel))
        .then((r) => {
          if (!r.ok) {
            missingSvg.add(rel);
            return null;
          }
          return r.text();
        })
        .catch(() => {
          missingSvg.add(rel);
          return null;
        }),
    );
  }
  return svgTextCache.get(rel);
}

/**
 * Drops decoded artwork so a long batch (the bake tool walks every template)
 * doesn't accumulate every SVG and bitmap in the account.
 */
export function clearRenderCaches() {
  imageCache.clear();
  svgTextCache.clear();
  recoloredCache.clear();
  missingSvg.clear();
  missingFiles.clear();
}

export function tintHex(tints) {
  if (!tints) return null;
  return tints["*"] || Object.values(tints)[0] || null;
}

/** Rewrites fill/stop colours in SVG source. */
export function recolorSvgText(text, tints) {
  return text.replace(FILL_PATTERN, (m, prefix, hex) => {
    const next = tints[hex.toLowerCase()] ?? tints["*"];
    return next ? `${prefix}${next}` : m;
  });
}

export function solidTintSvg(hex) {
  const fill = String(hex || "#ffffff").replace(/[^\w#]/g, "");
  return `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect width="64" height="64" fill="${fill}"/></svg>`;
}

function svgBlobImage(text) {
  const url = URL.createObjectURL(new Blob([text], { type: "image/svg+xml" }));
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  });
}

/**
 * Loads a vector with specific fills remapped.
 *
 * @param {string} rel
 * @param {Record<string,string>} tints  original fill -> replacement, both hex.
 *   Keyed per fill so multi-colour artwork keeps the fills that weren't changed.
 */
async function loadRecolored(rel, tints) {
  const key = `${rel}|${Object.entries(tints).sort().map(([k, v]) => `${k}>${v}`).join(",")}`;
  if (recoloredCache.has(key)) return recoloredCache.get(key);

  const promise = (async () => {
    const text = await svgText(rel);
    const hex = tintHex(tints);
    if (text) return svgBlobImage(recolorSvgText(text, tints));
    if (hex) return svgBlobImage(solidTintSvg(hex));
    return loadImage(rel);
  })();

  recoloredCache.set(key, promise);
  return promise;
}

/**
 * The distinct fills an SVG uses, in document order.
 *
 * Most vectors are a single flat shape, but 74 in this account use several
 * fills — up to 448. The UI needs the list so it can offer one control per
 * colour instead of overwriting detailed artwork with a single tint.
 */
export async function svgColors(rel) {
  const text = await svgText(rel);
  if (!text) return [];
  const found = [];
  const seen = new Set();
  for (const m of text.matchAll(FILL_PATTERN)) {
    const hex = m[2].toLowerCase();
    if (seen.has(hex)) continue;
    seen.add(hex);
    found.push(hex);
  }
  return found;
}

/** Registers a font file with the document and returns its family name. */
export function loadFont(rel) {
  if (!rel) return Promise.resolve(null);
  if (fontCache.has(rel)) return fontCache.get(rel);

  const family = `tpl-${[...rel].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7).toString(36)}`;
  const promise = new FontFace(family, `url("${fileUrl(rel)}")`)
    .load()
    .then((face) => {
      document.fonts.add(face);
      return family;
    })
    .catch(() => null);

  fontCache.set(rel, promise);
  return promise;
}

/** Preloads everything a model needs so the first paint isn't piecemeal. */
export async function preload(model, { fonts = true } = {}) {
  const files = [
    ...model.layers.filter((l) => l.kind !== "text" && l.file).map((l) => l.file),
    ...(model.brandedOverlays ?? []).map((o) => o.file),
  ];
  const images = files.map((file) => loadImage(file));
  const faces = fonts ? model.fontFiles.map((f) => loadFont(f)) : [];
  await Promise.all([...images, ...faces]);
}

function applyCase(text, layer) {
  return layer.caps ? text.toLocaleUpperCase() : text;
}

function decorate(text, layer) {
  let out = `${layer.prefix ?? ""}${text}${layer.suffix ?? ""}`;
  if (layer.betweenCharacters) out = [...out].join(layer.betweenCharacters);
  if (layer.betweenWords) out = out.split(/\s+/).join(layer.betweenWords);
  return out;
}

function setFont(ctx, size, family) {
  ctx.font = `${size}px "${family}", serif`;
}

/**
 * Picks the largest size that fits the element's box, clamped to the range the
 * template author allowed. Mirrors how the source stores `minSizePx`/`maxSizePx`
 * rather than a fixed size: short names render large, long ones shrink.
 */
function fitFontSize(ctx, lines, layer, family) {
  let size = layer.maxSize || 48;
  const min = Math.min(layer.minSize || 8, size);

  setFont(ctx, size, family);
  const widest = Math.max(...lines.map((l) => ctx.measureText(l).width), 1);
  if (layer.width > 0 && widest > layer.width) {
    size = Math.max(min, (size * layer.width) / widest);
  }

  const spacing = layer.lineSpacing || 1;
  const totalHeight = lines.length * size * spacing;
  if (layer.height > 0 && totalHeight > layer.height) {
    size = Math.max(min, (size * layer.height) / totalHeight);
  }
  return size;
}

function drawText(ctx, layer, value) {
  const raw = applyCase(decorate(value ?? "", layer), layer);
  if (!raw.trim()) return;

  const lines = layer.multiline ? raw.split(/\r?\n/) : [raw];
  const family = layer.family ?? "serif";

  const size = fitFontSize(ctx, lines, layer, family);
  setFont(ctx, size, family);

  ctx.save();
  ctx.translate(layer.centerX, layer.centerY);
  if (layer.rotation) ctx.rotate((layer.rotation * Math.PI) / 180);
  if (layer.skewX || layer.skewY) {
    ctx.transform(1, Math.tan(((layer.skewY ?? 0) * Math.PI) / 180), Math.tan(((layer.skewX ?? 0) * Math.PI) / 180), 1, 0, 0);
  }

  if (layer.tracking) {
    // Supported in current browsers; harmless where it isn't.
    ctx.letterSpacing = `${layer.tracking}px`;
    setFont(ctx, size, family);
  }

  ctx.textAlign = layer.align === "left" ? "left" : layer.align === "right" ? "right" : "center";
  ctx.textBaseline = "middle";
  ctx.globalAlpha = layer.alpha ?? 1;

  const x = layer.align === "left" ? -layer.width / 2 : layer.align === "right" ? layer.width / 2 : 0;
  const spacing = size * (layer.lineSpacing || 1);
  const startY = -((lines.length - 1) * spacing) / 2;

  lines.forEach((line, i) => {
    const y = startY + i * spacing;
    if (layer.outlineWidth > 0) {
      ctx.lineWidth = layer.outlineWidth * 2;
      ctx.strokeStyle = layer.outlineColor ?? "#000";
      ctx.lineJoin = "round";
      ctx.strokeText(line, x, y);
    }
    ctx.fillStyle = layer.color ?? "#000";
    ctx.fillText(line, x, y);
  });

  if (layer.tracking) ctx.letterSpacing = "0px";
  ctx.restore();
}

function drawImage(ctx, layer, img) {
  if (!img) return false;
  ctx.save();
  ctx.translate(layer.centerX, layer.centerY);
  if (layer.rotation) ctx.rotate((layer.rotation * Math.PI) / 180);
  ctx.globalAlpha = layer.opacity ?? 1;
  ctx.drawImage(img, -layer.width / 2, -layer.height / 2, layer.width, layer.height);
  ctx.restore();
  return true;
}

function fillLayer(ctx, layer, hex) {
  if (!hex) return;
  ctx.save();
  ctx.translate(layer.centerX, layer.centerY);
  if (layer.rotation) ctx.rotate((layer.rotation * Math.PI) / 180);
  ctx.globalAlpha = layer.opacity ?? 1;
  ctx.fillStyle = hex;
  ctx.fillRect(-layer.width / 2, -layer.height / 2, layer.width, layer.height);
  ctx.restore();
}

/**
 * The wrap colour, which stays live even when artwork comes from a plate.
 *
 * Shoppers change the background far more than any other colour, so it is
 * resolved from the buyer's tint and only falls back to the template default.
 */
function wrapColor(model, state) {
  const bg = model.layers.find((l) => l.kind === "vector" && l.isBackground);
  if (bg) {
    const chosen = tintHex(state.tints?.[bg.uuid]);
    if (chosen) return chosen;
    if (bg.defaultTint) return bg.defaultTint;
  }
  // Falling back to the colour the plate was cut from reproduces the wrap
  // exactly; the model's own bgColor can differ from what was harvested.
  return model.artworkPlateBg ?? model.bgColor ?? "#ffffff";
}

/**
 * How much of this template's artwork a prebuilt plate can stand in for.
 *
 * "full" replaces every non-text layer. "partial" covers only the layers
 * beneath the first photo slot, so the buyer's picture and anything above it
 * still composite normally. Returns null when no plate should be used.
 *
 * @returns {{ upTo: number } | null}  upTo is the exclusive layer index the
 *   plate accounts for.
 */
export function artworkPlateCoverage(model) {
  if (!model.artworkPlate) return null;

  // A plate is a flatten of the harvest, including anti-aliased cream/white
  // fringes around every shape. All-vector wraps don't need that flatten —
  // the SVGs already scale cleanly — and drawing both produces a white halo
  // around every path (Nordic Christmas on a red wrap is the obvious case).
  const hasRasterArtwork = model.layers.some((l) => l.kind === "image" && l.file && !l.uploadable);
  if (!hasRasterArtwork) return null;

  // Plates are painted across the whole sheet, so one cut at a different shape
  // would squash the artwork. Harvests are square; some templates are not.
  const pw = model.artworkPlateWidth;
  const ph = model.artworkPlateHeight;
  if (pw && ph && model.width && model.height) {
    const drift = Math.abs(pw / ph - model.width / model.height);
    if (drift > 0.01) return null;
  }

  const firstPhoto = model.layers.findIndex((l) => l.kind === "image" && l.uploadable);
  // Vector artwork (dinos, spots, hats) is not in the plate as a usable
  // layer — the harvest either tints it into the wrap colour or flattens it
  // so extraction strips it. Those SVGs still scale cleanly, so the plate
  // only stands in for what sits beneath the first live vector.
  const firstLiveVector = model.layers.findIndex((l) => l.kind === "vector" && !l.isBackground);

  const plateEnd = (() => {
    if (firstPhoto === -1 && firstLiveVector === -1) return model.layers.length;
    if (firstPhoto === -1) return firstLiveVector;
    if (firstLiveVector === -1) return firstPhoto;
    return Math.min(firstPhoto, firstLiveVector);
  })();

  if (firstPhoto !== -1 && model.artworkPlateMode !== "partial" && firstLiveVector === -1) {
    return null;
  }
  return { upTo: plateEnd };
}

function drawEmptySlot(ctx, layer) {
  ctx.save();
  ctx.translate(layer.centerX, layer.centerY);
  if (layer.rotation) ctx.rotate((layer.rotation * Math.PI) / 180);
  ctx.fillStyle = "rgba(124, 156, 255, 0.12)";
  ctx.strokeStyle = "rgba(124, 156, 255, 0.7)";
  ctx.lineWidth = Math.max(2, Math.min(layer.width, layer.height) * 0.012);
  ctx.setLineDash([ctx.lineWidth * 4, ctx.lineWidth * 3]);
  ctx.fillRect(-layer.width / 2, -layer.height / 2, layer.width, layer.height);
  ctx.strokeRect(-layer.width / 2, -layer.height / 2, layer.width, layer.height);
  ctx.restore();
}

/**
 * Buyer photos are fitted (cover or contain) then clipped to the hole.
 * Template artwork keeps stretching to the box — those files are already sized.
 */
function drawFittedImage(ctx, layer, img, mask, { zoom = 1, panX = 0, panY = 0, isolate = false } = {}) {
  // Theme-only addition (not in the studio copy): Product Builder designs cut a masked photo (a circle frame) on
  // its own canvas first. Masking straight on the sheet, as below, also erases whatever is already painted
  // under the photo's box, which leaves the corners of a circle empty in the print.
  if (mask && isolate) {
    const t = ctx.getTransform();
    const k = Math.hypot(t.a, t.b) || 1;
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(layer.width * k));
    c.height = Math.max(1, Math.round(layer.height * k));
    const x = c.getContext("2d");
    x.scale(c.width / layer.width, c.height / layer.height);
    x.translate(layer.width / 2, layer.height / 2);
    const r = fittedDrawRect(img.naturalWidth || img.width, img.naturalHeight || img.height, layer.width, layer.height, { fit: layer.fit ?? "cover", zoom, panX, panY });
    x.drawImage(img, r.x, r.y, r.w, r.h);
    x.globalCompositeOperation = "destination-in";
    x.drawImage(mask, -layer.width / 2, -layer.height / 2, layer.width, layer.height);
    ctx.save();
    ctx.translate(layer.centerX, layer.centerY);
    if (layer.rotation) ctx.rotate((layer.rotation * Math.PI) / 180);
    ctx.globalAlpha = layer.opacity ?? 1;
    ctx.drawImage(c, -layer.width / 2, -layer.height / 2, layer.width, layer.height);
    ctx.restore();
    return;
  }
  ctx.save();
  ctx.translate(layer.centerX, layer.centerY);
  if (layer.rotation) ctx.rotate((layer.rotation * Math.PI) / 180);
  ctx.globalAlpha = layer.opacity ?? 1;

  ctx.beginPath();
  ctx.rect(-layer.width / 2, -layer.height / 2, layer.width, layer.height);
  ctx.clip();

  const r = fittedDrawRect(img.naturalWidth || img.width, img.naturalHeight || img.height, layer.width, layer.height, {
    fit: layer.fit ?? "cover",
    zoom,
    panX,
    panY,
  });
  ctx.drawImage(img, r.x, r.y, r.w, r.h);

  if (mask) {
    ctx.globalCompositeOperation = "destination-in";
    ctx.drawImage(mask, -layer.width / 2, -layer.height / 2, layer.width, layer.height);
  }
  ctx.restore();
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {object} model  render model from /api/templates/:id/model
 * @param {object} state  { values, fonts, colors, choices } keyed by field/uuid
 * @param {object} [opts]
 * @param {number} [opts.scale]  the print side is 6000px square, so on-screen
 *   rendering scales the whole scene down rather than painting it 1:1
 * @param {{x:number,y:number,w:number,h:number}} [opts.window]  theme-only addition (not in the studio copy):
 *   paint just this part of the sheet, in model px. Lets a 6000px print be built in quarters on phones whose
 *   canvases can't hold the whole sheet.
 */
export async function render(canvas, model, state = {}, { scale = 1, emptySlots = true, window: win = null } = {}) {
  const ctx = canvas.getContext("2d");
  const width = Math.max(1, Math.round((win ? win.w : model.width) * scale));
  const height = Math.max(1, Math.round((win ? win.h : model.height) * scale));
  canvas.width = width;
  canvas.height = height;

  ctx.clearRect(0, 0, width, height);
  ctx.save();
  if (scale !== 1) ctx.scale(scale, scale);
  if (win) ctx.translate(-win.x, -win.y);

  if (model.bgColor) {
    ctx.fillStyle = model.bgColor;
    ctx.fillRect(0, 0, model.width, model.height);
  }

  if (model.base) {
    const img = await loadImage(model.base);
    if (img) ctx.drawImage(img, 0, 0, model.width, model.height);
  }

  // A plate carries the artwork at print resolution with the wrap colour
  // removed, so the colour is painted first and the text still draws on top.
  const plate = artworkPlateCoverage(model);
  if (plate) {
    // A baked plate is the harvest as-is (gradient or patterned wrap). Painting
    // a wrap colour underneath would show through its holes and muddy it.
    if (model.artworkPlateMode !== "baked") {
      ctx.fillStyle = wrapColor(model, state);
      ctx.fillRect(0, 0, model.width, model.height);
    }
    const img = await loadImage(model.artworkPlate);
    if (img) ctx.drawImage(img, 0, 0, model.width, model.height);
  }

  for (let i = 0; i < model.layers.length; i += 1) {
    const layer = model.layers[i];
    if (plate && layer.kind !== "text" && i < plate.upTo) continue;

    if (layer.kind === "text") {
      // Per-slot settings win over the field's, so a field can be split into
      // individual entries without disturbing the grouped case.
      const value = state.elementValues?.[layer.uuid] ?? state.values?.[layer.field] ?? "";
      const fontFile = state.elementFonts?.[layer.uuid] ?? state.fonts?.[layer.field] ?? layer.font;
      const color = state.elementColors?.[layer.uuid] ?? state.colors?.[layer.field] ?? layer.color;
      const family = fontFile ? await loadFont(fontFile) : null;
      drawText(ctx, { ...layer, family: family ?? "serif", color }, value);
      continue;
    }

    if (layer.kind === "vector") {
      const file = state.choices?.[layer.uuid] ?? layer.file;
      const tints = state.tints?.[layer.uuid];
      const hasTint = tints && Object.keys(tints).length > 0;
      const img = hasTint ? await loadRecolored(file, tints) : await loadImage(file);
      if (!drawImage(ctx, layer, img)) {
        // Only the wrap background may collapse to a solid rect. Colour N
        // artwork (trees, icons) is path data — filling the box would hide it.
        const hex = tintHex(tints) || (layer.isBackground ? layer.defaultTint || model.bgColor : null);
        fillLayer(ctx, layer, hex);
      }
      continue;
    }

    if (layer.kind === "image") {
      const photo = state.elementPhotos?.[layer.uuid] ?? state.photos?.[layer.field];
      if (photo?.image) {
        const mask = layer.mask ? await loadImage(layer.mask) : null;
        drawFittedImage(ctx, layer, photo.image, mask, { ...photo, isolate: !!model.builder });
      } else if (layer.file) {
        const file = state.choices?.[layer.uuid] ?? layer.file;
        const img = await loadImage(file);
        if (img) {
          const mask = layer.mask ? await loadImage(layer.mask) : null;
          // Theme-only addition: a builder tile repeat is copies butted edge to edge. Scaled down on screen their
          // edges land between pixels and the paper shows through as a hairline, so each copy reaches one screen
          // pixel past its box. The print (scale 1) is untouched.
          const k = Math.hypot(ctx.getTransform().a, ctx.getTransform().b) || 1;
          const bleed = model.builder && !mask && !layer.rotation && k < 0.99 ? 1 / k : 0;
          const box = bleed ? { ...layer, width: layer.width + bleed * 2, height: layer.height + bleed * 2 } : layer;
          drawFittedImage(ctx, box, img, mask, { isolate: !!model.builder });
        }
      } else if (layer.uploadable && emptySlots) {
        drawEmptySlot(ctx, layer);
      }
    }
  }

  // Ring the slot whose input has focus, so a split field is navigable.
  const marked = state.highlight && model.layers.find((l) => l.uuid === state.highlight);
  if (marked) {
    const pad = Math.max(marked.width, marked.height) * 0.18 + 6 / scale;
    ctx.save();
    ctx.translate(marked.centerX, marked.centerY);
    if (marked.rotation) ctx.rotate((marked.rotation * Math.PI) / 180);
    ctx.lineWidth = 3 / scale;
    ctx.strokeStyle = "#7c9cff";
    ctx.setLineDash([10 / scale, 6 / scale]);
    ctx.strokeRect(-marked.width / 2 - pad, -marked.height / 2 - pad, marked.width + pad * 2, marked.height + pad * 2);
    ctx.restore();
  }

  ctx.restore();
  return canvas;
}

/** Renders to a detached canvas, for export at any resolution. */
export async function renderAtScale(model, state, scale) {
  return render(document.createElement("canvas"), model, state, { scale });
}
